import { z } from "zod"

import type {
  ResolvedTheme,
  ThemeColorMode,
  ThemeColorTokenKey,
  ThemeTokenKey,
  ThemeTokens,
} from "./types"

/** Color tokens that may carry an explicit dark-mode override. */
export const THEME_COLOR_TOKEN_KEYS: ThemeColorTokenKey[] = [
  "primary",
  "background",
  "foreground",
  "accent",
  "muted",
]

/**
 * The default theme token surface (~6-8 vars, US-012). Any missing/invalid token
 * in tenant config falls back to these values so a page is never broken. The
 * resolver in packages/theming maps these onto the shadcn CSS vars.
 */
export const DEFAULT_THEME_TOKENS: Required<Pick<ThemeTokens, ThemeTokenKey>> =
  {
    primary: "oklch(0.205 0 0)",
    background: "oklch(1 0 0)",
    foreground: "oklch(0.145 0 0)",
    accent: "oklch(0.97 0 0)",
    muted: "oklch(0.97 0 0)",
    radius: "0.625rem",
    // Points at one of the platform's preloaded font CSS-vars (US-012). The layout
    // declares --font-sans-a/b/c/d at module scope via next/font; the tenant token
    // selects which is active. Default = --font-sans-a (the platform sans). Never
    // `var(--font-sans)` itself (that would be self-referential once the theming
    // resolver maps fontFamily → --font-sans).
    fontFamily: "var(--font-sans-a)",
  }

export const THEME_TOKEN_KEYS = Object.keys(
  DEFAULT_THEME_TOKENS
) as ThemeTokenKey[]

/** The default theme as a fully-resolved value (light defaults, no dark overrides). */
export const DEFAULT_RESOLVED_THEME: ResolvedTheme = {
  ...DEFAULT_THEME_TOKENS,
  dark: {},
}

/**
 * Thin zod layer for the storefront-owned theme fields the backend does not
 * guarantee. Each token is optional; `resolveThemeTokens` fills gaps from the
 * default theme, guaranteeing token completeness.
 */
const darkTokensSchema = z
  .object({
    primary: z.string().optional(),
    background: z.string().optional(),
    foreground: z.string().optional(),
    accent: z.string().optional(),
    muted: z.string().optional(),
  })
  .partial()

export const themeTokensSchema = z
  .object({
    primary: z.string().optional(),
    background: z.string().optional(),
    foreground: z.string().optional(),
    accent: z.string().optional(),
    muted: z.string().optional(),
    radius: z.string().optional(),
    fontFamily: z.string().optional(),
    /** Explicit dark-mode color overrides; omitted tokens fall back to derived. */
    dark: darkTokensSchema.nullish(),
  })
  .partial()
  .catch({})

export const THEME_COLOR_MODES = ["both", "light", "dark"] as const

export const DEFAULT_THEME_COLOR_MODE: ThemeColorMode = "both"

/**
 * Narrow the tenant's `theme.colorMode`. Absent, unknown (e.g. a value a newer
 * cater app added) or malformed → `both`, per the contract.
 */
export function resolveThemeColorMode(raw: unknown): ThemeColorMode {
  const value =
    raw && typeof raw === "object"
      ? (raw as { colorMode?: unknown }).colorMode
      : undefined
  return (THEME_COLOR_MODES as readonly unknown[]).includes(value)
    ? (value as ThemeColorMode)
    : DEFAULT_THEME_COLOR_MODE
}

/** Validate + complete tenant theme tokens, falling back to defaults per-token. */
export function resolveThemeTokens(raw: unknown): ResolvedTheme {
  const parsed = themeTokensSchema.parse(raw ?? {})
  const resolved: ResolvedTheme = { ...DEFAULT_THEME_TOKENS, dark: {} }
  for (const key of THEME_TOKEN_KEYS) {
    const value = parsed[key]
    if (typeof value === "string" && value.length > 0) {
      resolved[key] = value
    }
  }
  // Carry through only the non-empty explicit dark overrides; anything absent
  // stays derived downstream (see packages/theming renderThemeCss).
  for (const key of THEME_COLOR_TOKEN_KEYS) {
    const value = parsed.dark?.[key]
    if (typeof value === "string" && value.length > 0) {
      resolved.dark[key] = value
    }
  }
  return resolved
}
